import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  LocationOn,
  ShortText,
  Category,
  FlightTakeoff,
  FlightLand,
  TransferWithinAStation,
  ExpandMore,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AIRPORTS,
  EDIT_AIRPORT,
} from '../../component/common/Routes/AppRoutes';
import TextInputField from '../../component/ui/FormFields/TextInputField';
import CustomButton from '../../component/ui/FormFields/Button';
import StatusRadioGroup from '../../component/ui/FormFields/StatusRadioGroup';
import { useForm } from 'react-hook-form';
import Loading from '../../component/ui/Loading';
import {
  resetAirportState,
  updateAirportApprovalStatusFailure,
  updateAirportApprovalStatusRequest,
  updateAirportApprovalStatusSuccess,
  viewAirportRequest,
} from '../../redux/Airports/actions/airportActions';
import { updateAirportApprovalStatusAPI } from '../../redux/Airports/sagas/airportSaga';
import { toast, ToastContainer } from 'react-toastify';
import AirportApproval from './AirportApprovalPage';
import '../../styles/UserStyles.css';

export default function ViewAirport() {
  const { control } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
    const location = useLocation();
    const tab = location.state.tab ?? 0;
    const page= location.state.page?? 1;
    const searchQuery=location.state.searchQuery??"";
  const { airportId } = useParams();
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const {
    loading: viewLoading,
    data: airportData,
    error,
  } = useSelector((state) => state?.airport?.airportDetails || {});

  const user = useSelector((state) => state?.auth?.user);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === 'string'
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const [approvalStatus, setApprovalStatus] = useState('');
  const [initialStatus, setInitialStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const airportApprove = useSelector(
    (state) => state?.airport?.updateAirport || {}
  );

  const successMessage = airportApprove?.successMessage;
  const errorMessage = airportApprove?.errorMessage;

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage, {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetAirportState());
          navigate(AIRPORTS);
        },
      });
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetAirportState());
        },
      });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (airportId) {
      dispatch(viewAirportRequest(airportId));
    }
  }, [airportId]);
 
  useEffect(() => {
    if (airportData?.data?.approoved_status) {
      setApprovalStatus(airportData?.data?.approoved_status);
      setInitialStatus(airportData?.data?.approoved_status);
      setRejectionReason(airportData?.data?.rejection_reason || '');
    }
  }, [airportData]);

  if (viewLoading) {
    return <Loading text='Loading Airport detail...' />;
  }

  const servicesId =
    airportData?.data?.AiportServices?.map((service) =>
      service?.Service?.arrival_departure_type !== 3
        ? `${service?.Service?.services} - ${service?.Service?.type_of_name} - ${service?.Service?.type_of_service}`
        : `${service?.Service?.services} - ${
            service?.Service?.type_of_name
          } - ${service?.Service?.type_of_service} - ${
            service?.Service?.arrival_departure_type === 3
              ? service?.Service?.transit_type_of_service
              : 'Unknown'
          }`
    ) || [];

  const airportServiceList =
    airportData?.data?.AiportServices?.map((value) => ({
      value: value?.price || '-',
      label:
        value?.Service?.arrival_departure_type !== 3
          ? `${value?.Service?.services} -${value?.Service?.type_of_name} - ${value?.Service?.type_of_service}`
          : `${value?.Service?.services} - ${value?.Service?.type_of_name} - ${
              value?.Service?.type_of_service
            } - ${
              value?.Service?.arrival_departure_type === 3
                ? value?.Service?.transit_type_of_service
                : 'Unknown'
            }`,
      serviceType: value?.Service?.arrival_departure_type || '1',
      arrivalDescription: value?.arrival || '-',
      departureDescription: value?.departure || '-',
      transitDescription: value?.transit || '-',
    })) || [];

  if (error) {
    return (
      <Box textAlign='center' py={5}>
        <Typography variant='h6' color='error'>
          {error || 'Failed to fetch airport data.'}
        </Typography>
        <CustomButton
          text='Back to Airports'
          onClick={() => navigate(AIRPORTS)}
        />
      </Box>
    );
  }

  if (!airportData) {
    return (
      <Box textAlign='center' py={5}>
        <Typography variant='h6' color='error'>
          {'No Airport data found!'}
        </Typography>
        <CustomButton
          text='Back to Airports'
          onClick={() => navigate(AIRPORTS)}
        />
      </Box>
    );
  }

  const fields = [
    {
      label: 'Airport Name',
      value: airportData?.data?.name || '-',
      icon: <LocationOn />,
      xs: 12,
      sm: 6,
    },
    {
      label: 'Airport Code',
      value: airportData?.data?.short_name || '-',
      icon: <ShortText />,
      xs: 12,
      sm: 6,
    },
    {
      label: 'Airport Type',
      value: airportData?.data?.airport_type || '-',
      icon: <Category />,
      xs: 12,
      sm: 6,
    },
    {
      label: 'Service ',
      value: servicesId || '-',
      icon: <Category />,
      multiple: true,
      xs: 12,
      sm: 6,
    },
    {
      label: 'Country',
      value: airportData?.data?.country_name || '-',
      icon: <LocationOn />,
      xs: 12,
      sm: 6,
    },
    {
      label: 'State',
      value: airportData?.data?.state_name || '-',
      icon: <LocationOn />,
      xs: 12,
      sm: 6,
    },
  ];

  if (airportData?.data?.country_name === 'India') {
    fields.push(
      {
        label: 'SGST (%)',
        value: airportData?.data?.sgst || '-',
        xs: 12,
        sm: 6,
      },
      {
        label: 'CGST (%)',
        value: airportData?.data?.cgst || '-',
        xs: 12,
        sm: 6,
      }
    );
  } else {
    fields.push({
      label: 'IGST',
      value: airportData?.data?.tax || '-',
      xs: 12,
      sm: 12,
    });
  }

  const showPendingOption = initialStatus === '2' || initialStatus === 2;

  const status = airportData?.data?.status === 1 ? 'Active' : 'Inactive' || '-';

  const approvalStatusText =
    airportData?.data?.approoved_status === 1
      ? 'Approved'
      : airportData?.data?.approoved_status === 2
      ? 'Pending'
      : 'Rejected';

  const handleApprovalStatusChange = (e) => {
    setApprovalStatus(e.target.value);
  };

  const handleApprovalStatusSubmit = async () => {
    const payload = {
      airport_id: airportData?.data?.id,
      approval_status: approvalStatus,
      rejection_reason: rejectionReason,
    };

    dispatch(updateAirportApprovalStatusRequest());

    try {
      const data = await updateAirportApprovalStatusAPI(
        payload.airport_id,
        payload.approval_status,
        payload.rejection_reason
      );
      dispatch(updateAirportApprovalStatusSuccess(data));
    } catch (error) {
      dispatch(updateAirportApprovalStatusFailure(error.message));
    }
  };

  return (
    <>
      <ToastContainer />
      <Box className='container'>
        <Card>
          <CardContent sx={{ padding: 4 }}>
            <Typography variant='h5' className='form-heading' gutterBottom>
              View Airport
            </Typography>
            <Grid container spacing={3} className='service-form-margin'>
              {fields?.map((field, index) => (
                <Grid item xs={field.xs || 12} sm={field.sm || 6} key={index}>
                  <TextInputField
                    label={field.label}
                    multiple={field.multiple}
                    value={
                      Array.isArray(field.value)
                        ? field.value.join(', ')
                        : field.value
                    }
                    disabled={true}
                    startAdornment={
                      <InputAdornment position='start'>
                        {field.icon}
                      </InputAdornment>
                    }
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Typography variant='h6' sx={{ mt: 2, mb: 2 }}>
                  Service Details
                </Typography>
                {airportServiceList.map((service, index) => (
                  <Accordion key={index} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls={`service-panel-${index}-content`}
                      id={`service-panel-${index}-header`}
                    >
                      <Typography variant='subtitle1'>
                        {service.label}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextInputField
                            label='Price'
                            value={service.value}
                            disabled={true}
                          />
                        </Grid>

                        {service.serviceType === 3 ? (
                          <Grid item xs={12}>
                            <TextInputField
                              label='Transit Description'
                              value={service.transitDescription}
                              disabled={true}
                              multiline={true}
                              minRows={3}
                              startAdornment={
                                <InputAdornment position='start'>
                                  <TransferWithinAStation />
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        ) : (
                          <>
                            <Grid item xs={12} sm={6}>
                              <TextInputField
                                label='Arrival Description'
                                value={service.arrivalDescription}
                                disabled={true}
                                multiline={true}
                                minRows={3}
                                startAdornment={
                                  <InputAdornment position='start'>
                                    <FlightLand />
                                  </InputAdornment>
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextInputField
                                label='Departure Description'
                                value={service.departureDescription}
                                disabled={true}
                                multiline={true}
                                minRows={3}
                                startAdornment={
                                  <InputAdornment position='start'>
                                    <FlightTakeoff />
                                  </InputAdornment>
                                }
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>

              {/* Status and Approval Status row */}
              <Grid item xs={12}>
                <Grid container spacing={4} direction='row'>
                  <Grid item xs={12} sm={6}>
                    <StatusRadioGroup
                      name={'status'}
                      control={control}
                      defaultValue={status}
                      disabled={true}
                    />
                  </Grid>
                  {user?.role === 'Admin' && (
                    <Grid item xs={12} lg={3}>
                      <Typography variant='subtitle1' gutterBottom>
                        Approval Status
                      </Typography>
                      <RadioGroup
                        value={approvalStatus}
                        onChange={handleApprovalStatusChange}
                        row
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio color='success' />}
                          label='Approved'
                        />
                        {showPendingOption && (
                          <FormControlLabel
                            value={2}
                            control={<Radio color='warning' />}
                            label='Pending'
                          />
                        )}
                        <FormControlLabel
                          value={3}
                          control={<Radio color='error' />}
                          label='Rejected'
                        />
                      </RadioGroup>

                      <CustomButton
                        variant='contained'
                        className='success-button'
                        text='Submit Approval Status'
                        onClick={handleApprovalStatusSubmit}
                      />
                    </Grid>
                  )}

                  {approvalStatus === '3' && (
                    <Grid item xs={12}>
                      <Box className='rejection-reason-box'>
                        <Typography
                          variant='subtitle1'
                          color='error'
                          gutterBottom
                        >
                          Rejection Reason
                        </Typography>
                        <TextInputField
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          required
                          multiline
                          rows={3}
                          placeholder='Enter reason for rejection...'
                        />
                      </Box>
                    </Grid>
                  )}
                  {airportData?.data?.approoved_status === 3 &&
                    airportData?.data?.rejection_reason && (
                      <Grid item xs={12}>
                        <Box className='rejection-reason-box'>
                          <Typography
                            variant='subtitle1'
                            color='error'
                            gutterBottom
                          >
                            Rejection Note
                          </Typography>
                          <Typography variant='body1'>
                            {airportData?.data?.rejection_reason}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  {user?.role === 'Admin' &&
                    airportData?.data?.airportUpdateLogs?.length > 0 && (
                      <Grid item xs={12} lg={3}>
                        <Typography variant='subtitle1' gutterBottom>
                          Staff Edit Approval
                        </Typography>

                        <Grid item xs={12}>
                          <CustomButton
                            variant='contained'
                            className='success-button'
                            text='Staff Edit Approval'
                            onClick={handleOpen}
                          />
                          <Modal
                            open={open}
                            onClose={handleClose}
                            aria-labelledby='modal-modal-title'
                            aria-describedby='modal-modal-description'
                          >
                            <Box
                              className='booking-modal'
                              sx={{ width: '1000px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <AirportApproval
                                airportId={airportId}
                                handleClose={handleClose}
                              />
                            </Box>
                          </Modal>
                        </Grid>
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>

            <Box className='button-container' sx={{ marginTop: 4 }}>
              <CustomButton
                variant='contained'
                className='cancel-button'
                text='Back'
                  onClick={() => {
                  navigate(`/airports`,{state:{tab:tab,page:page,searchQuery:searchQuery}})
                
                }}
                link={AIRPORTS}
              />
              {(permissions?.airports?.edit || user?.user_type === 1) && (
                <CustomButton
                  variant='contained'
                  className='success-button'
                  text='Edit'
                  onClick={() =>
                    navigate(`${EDIT_AIRPORT}/${airportData?.data?.id}`)
                  }
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
